// THE ECONOMY: what everything in the game is worth, in SHIFTS (Matt, after
// playtest 12: "take this economy and figure out how to apply it across all
// actions, cards, everything... price everything according to a consistent
// economy"). Pure tables and arithmetic; `tools/economy/price.mjs` prices the
// whole game with them into docs/ECONOMY.md, and src/test/economy.test.js
// fails when something new lands off the line without being a BARGAIN.
//
// THE UNIT IS ONE STEWARD'S SHIFT - half a person's day - because a shift is
// the one thing every resource can be bought with: a shift in a field is 2
// food, in a workshop 2 goods. Everything else follows by the trades the game
// already offers (DESIGN.md "Proposal: one unit of value").
//
// The standard is the price of the ORDINARY trade, not a cap. Excitement is a
// deliberate break from it - a combo, a good roll, the right timing - and
// every one of those is named in BARGAINS, so "this is cheap on purpose" and
// "this is cheap by accident" can never be confused (Matt: "how do we make
// sure that there are still opportunities for someone to find extra value...
// like you've achieved something clever?").

/** One of each, in shifts. */
export const VALUE = {
  shifts: 1,
  workers: 2, // a person's day: two shifts, two Frontier actions, two Hall cards
  actions: 1, // a Frontier action is half a worker
  card: 1, // a card in the Hall's hand: a worker deals two
  food: 0.5, // a field: a shift for 2
  goods: 0.5, // a workshop: a shift for 2
  coin: 0.5, // the market: 2 goods and a shift for 4
  knowledge: 1, // Study, one library: a worker for 2
  happiness: 1, // the Hall's own plays: Rally, a card for 1; Song, a card and a coin for 2
  // A person turns out a worker (2 shifts) every dawn and eats half a food a
  // night; rationed by homes and the crowd's grumbling. Counted over a few
  // nights, not forever, because the town's size is capped by its houses.
  population: 6,
  strength: 3, // a warband: 2 goods and two shifts for 1 strength
  threat: -2, // a raider's point of strength, walking at you: what it wrecks, roughly
};

/** How many nights a lasting effect is counted over. A year is twelve. */
export const HORIZON = 12;
/**
 * How often a card bought from the shelf gets played in the year it is
 * bought (playtest 14: buy once, play free). A deck of twelve to fifteen,
 * three plays a day, and the card arrives mid-year: it comes round about
 * twice. So a shelf card's price buys PLAYS_PER_YEAR plays, each worth what
 * the card gives beyond the seat it takes (VALUE.card).
 */
export const PLAYS_PER_YEAR = 2;

/**
 * What one point of each standing effect (`modsOf`) is worth PER NIGHT, in
 * shifts, with how often it comes up. Estimates - written down so they can
 * be argued with, which is the point.
 */
export const MOD_VALUE = {
  // Each is (what one point adds to one use) x (uses a night), the uses read
  // off a real table: playtest 12's twelve nights (docs/ECONOMY.md, "How
  // often"). Study ran 0.5 a night, so +1 knowledge on Study is 0.5.
  nightKnowledge: 1, goodsNight: 0.5, foodNight: 0.5, // every night, no use needed
  levyWorkers: 1, // a levy about every other night (Hall plays: 2.25 a night), a worker (2) each
  bandDice: 0.8, // a fight 0.83 a night; a die more is ~0.5 hits, a hit takes 2 shifts of threat
  menaceSlow: 0.7, // half the raiders: ~1.5 shifts of threat a night, halved
  festivalJoy: 0.6, // a festival card ~0.6 a night, +1 happiness each
  study: 0.5, // Study 0.5 a night, +1 knowledge
  ponder: 0.4, // Ponder 0.42 a night
  births: 0.4, // a family a little more often
  marketCap: 0.2, marketCoin: 0.3, // a market shift ~0.6 a night (a third of Work), +1 coin
  roadLevel: 0.5, // a joined village or two +1 a night each, and roads go further
  marchRange: 0.3, // moves 2.9 a night; the rough steps among them come cheaper
  bandMax: 0.3, songTwice: 0.3, garrisonDice: 0.3, villageDice: 0.3, levyHappiness: 0.3,
  parleyHalf: 0.25, // a parley 0.25 a night, ~1 shift of coin saved
  studyCap: 0.2, townDice: 0.2, rally: 0.3,
  hallJoy: 0.3, // a rite or a song ~0.3 a night in year 1, +1 happiness each
  envoyTwice: 0.15, sight: 0.1, caravanCap: 0.1,
  // huntBase is +1 on hunts AND +1 food on every staffed field (fieldFood):
  // a field shift ~0.9 a night, +1 food (0.5) each, and the rare hunt.
  huntBase: 0.5,
  caravanGoods: 0.08, grainFood: 0.08, huntDice: 0.08, // caravans, grain and hunts were rare (~0.1-0.2 a night)
  larder: 0.05, // per food of room: Granaries' 4 is 0.2
  // These run the other way: a point DOWN is the good one (hit on 3+ not
  // 4+, the great work cheaper, workers halve later), so they are negative.
  hitOn: -0.7, // a fight 0.83 a night: hitting on one less is ~0.4 hits a fight, plus the defence dice
  techCost: -0.6, // seven technologies in twelve nights: a knowledge each
  unhappyAt: -0.3, buildWorkers: -0.2, buildGoods: -0.1, buildCoin: -0.1, famineHappiness: -0.1,
  fever: -0.05, // per point in 10; Medicine's -3 is +0.15
  unrest: -0.1,
};

/** A technology that opens a building or a branch is worth having for that alone. */
export const UNLOCK_VALUE = 4;
/** ...and one that later technologies need is worth a little for each (up to three). */
export const GATEWAY_VALUE = 1;
/** A bad card taken out of the deck: it would have come round about once a year. */
export const REMOVED_PER_YEAR = 1;

/**
 * The deliberate breaks from the line - the clever moves. Each says WHY, and
 * the price list shows them as bargains, not mistakes.
 */
export const BARGAINS = {
  'trade:haggle': 'needs no building, so it pays half: the floor a shift never falls below',
  'card:grow': 'a person is the engine; the price is the homes and the grumbling, not the card',
  'card:feast': 'the start deck\'s one treat: a rally that the world remembers',
  'culture:cull': 'deck-building\'s own trick: a weak card out, for a coin (playtest 13: free was too easy on grievances)',
  'card:elders_speak': 'the draw card: free since playtest 14 like every play, and the deck-builder\'s way to see more of itself',
  'military:repair-village': 'a wrecked village is the raiders\' damage; mending it is meant to be quick',
  'card:old_custom': 'the starting deck\'s dead weight, there to be trashed (or kept, under Tradition)',
  'card:grievance': 'a bad card on purpose: what a famine leaves behind',
  'card:mourning': 'a bad card on purpose: what a loss leaves behind',
  'event:envoys': 'a peace offer: turning them away is the road to war, and meant to be',
  'card:grudge': 'a bad card on purpose: what a refusal leaves behind',
  'military:found': 'a village sends its ground\'s yield every night: the Frontier\'s long game',
  'science:publish': 'a theory that holds pays double and a student: the Scholars\' reward for a right deduction',
  'science:ponder': 'a die, no library: worse than Study on average, which is its argument',
  'military:parley': 'a gamble: two in three it works',
  'card:levy': 'free workers today, paid for in happiness that grows with the town (CROWD)',
  'tech:writing': 'the knowledge foundation: Study, the laws, the Scholar - cheap so the tree can start',
  'tech:irrigation': 'the food foundation: fields grow more - cheap, or the first year starves (the bots did, at 7)',
  'tech:pottery': 'the stores foundation: grain and the Warehouse - cheap so the town can keep what it makes',
  'tech:kilns': 'the way up the ladder: priced to open tier 3, its worth is everything above it',
  'tech:distilling': 'the way up the ladder: priced to open tier 4',
  'tech:assaying': 'the way up the ladder: priced to open tier 5',
};

/**
 * What a role's own token is worth: the Stewards count in shifts, the
 * Frontier in actions (both half a worker); the Scholars and the Hall in
 * whole workers. `workers` in a cost means the role's token.
 */
export const TOKEN = { trade: 1, military: 1, science: 2, culture: 2 };
export const costOf = (role, cost = {}) => valueOf({ ...cost, workers: 0 }) + (cost.workers || 0) * (TOKEN[role] ?? VALUE.workers);

/** Shifts for a patch of resources ({ food: 2, goods: -1, ... }). Unknown keys count nothing. */
export const valueOf = (patch = {}) => Object.entries(patch || {})
  .reduce((n, [k, v]) => n + (typeof v === 'number' ? v * (VALUE[k] ?? 0) : 0), 0);

/** Shifts a standing set of mods is worth per night. */
export const modsPerNight = (mods = {}) => Object.entries(mods || {})
  .reduce((n, [k, v]) => n + (typeof v === 'number' ? v * (MOD_VALUE[k] ?? 0) : 0), 0);

/** The expected face of a die {faces, base}: faces read base+1 .. base+faces. */
export const dieMean = ({ faces, base = 0 }) => base + (faces + 1) / 2;

/** How a gain compares with its cost: 1 is fair, above is a deal, below a loss. */
export const ratio = (gain, cost) => (cost > 0 ? gain / cost : (gain > 0 ? Infinity : 1));

/** Off the line: a deal or a loss beyond these, unless it is a named bargain. */
export const FAIR = { low: 0.75, high: 1.4 };
